import { useFormikContext } from "formik";
import FormikSubmitButton from "./FormikSubmitButton";
// @ts-expect-error pure js
import ElectronicSigning from "../ElectronicSigning/ElectronicSigning";
// @ts-expect-error pure js
import RadioGroup from "./RadioGroup";
// @ts-expect-error pure js
import Radio from "./Radio";
import Card from "./Card";
import Callout from "./Callout";
import FeaturedIcon from "./FeaturedIcon";
import {
  RECOMMANDE_PRICE,
  SIGNATURE_PRICE,
  // @ts-expect-error pure js
} from "../../models/signatureElectronique";
// @ts-expect-error pure js
import useAccount from "../../hooks/use-account";
import Button from "./Button";
import Text from "./Text";
import Title from "./Title";
// @ts-expect-error pure js
import Tabs from "./Tabs";
// @ts-expect-error pure js
import { ProfileStatus } from "../../models/profile";
import {
  IconBallpen,
  IconCheck,
  IconDeviceDesktopCheck,
  IconEye,
  IconMail,
  IconMailbox,
  IconSignature,
} from "@tabler/icons-react";
// @ts-expect-error pure js
import SendRecommande from "../Laposte/SendRecommande";
import DrawnSignatures, { type Signatory } from "../Edl/DrawnSignatures";
// @ts-expect-error pure js
import { classnames, showFormikFieldsInError } from "../../utils";
import { ModalDialogBody, ModalDialogFooter } from "./ModalDialog";
import DocumentDownloadLink from "./DocumentDownloadLink";
// @ts-expect-error pure js
import DocumentThumbnail from "./DocumentThumbnail";
import type { Profile } from "../Profile/types";
import type { Tenant } from "../../models/tenant";
import styles from "./ChooseSendFileMethod.module.css";

interface ChooseSendFileMethodValues {
  type_envoi?: string;
}

interface ChooseSendFileMethodProps {
  propertyId?: number;
  error?: string;
  profiles?: Profile[];
  caution?: boolean;
  tenant?: Tenant;
  typeSend?: string;
  drawnSignatories?: Signatory[];
  onPreview?(values: ChooseSendFileMethodValues): void | Promise<void>;
  previewUrl?: string;
  sticky?: boolean;
}

export default function ChooseSendFileMethod({
  propertyId,
  error,
  profiles,
  caution,
  tenant,
  typeSend = "elec",
  drawnSignatories,
  onPreview,
  previewUrl,
  sticky,
}: ChooseSendFileMethodProps) {
  const { account } = useAccount() as { account: { credits: number } };
  const { values, validateForm, setFieldTouched, isSubmitting, setSubmitting } =
    useFormikContext<ChooseSendFileMethodValues>();
  const { selectPreviousTab } = Tabs.useActiveTab();

  async function handlePreview() {
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      showFormikFieldsInError(errors, setFieldTouched);
      return;
    }
    setSubmitting(true);
    await onPreview?.(values);
    setSubmitting(false);
  }

  const sendMethods = [
    typeSend === "lrar"
      ? {
          value: "lrar",
          icon: IconMailbox,
          title: "Lettre recommandée",
          description: "Envoyée par La Poste, avec accusé de réception",
        }
      : {
          value: "elec",
          icon: IconSignature,
          title: "Signature électronique",
          description: "Signature certifiée à valeur légale, à distance",
        },
    {
      value: "mail",
      icon: IconMail,
      title: "Envoyer par mail",
      description: "Le locataire reçoit le document par e-mail",
    },
    ...(drawnSignatories && drawnSignatories.length > 0
      ? [
          {
            value: "numerique",
            icon: IconBallpen,
            title: "Signature numérique",
            description: "Signature sur place, sur votre appareil",
          },
        ]
      : []),
    {
      value: "generer",
      icon: IconDeviceDesktopCheck,
      title: "Créer sans envoi",
      description: "Génère le document sans l'envoyer",
    },
  ];

  const previewButton = onPreview && (
    <Button
      type="button"
      icon={IconEye}
      isLoading={isSubmitting}
      disabled={isSubmitting}
      onClick={handlePreview}
      className={styles.previewButton}
    >
      Prévisualiser le document
    </Button>
  );
  const validerButton = (
    <FormikSubmitButton
      disabled={
        (values.type_envoi === "elec" && account.credits < SIGNATURE_PRICE) ||
        (values.type_envoi === "lrar" && account.credits < RECOMMANDE_PRICE)
      }
    >
      Valider
    </FormikSubmitButton>
  );

  const content = (
    <div className={styles.content}>
      {previewButton}
      <RadioGroup styling="card">
        {sendMethods.map(({ value, icon: Icon, title, description }) => {
          const isSelected = values.type_envoi === value;
          return (
            <div key={value} className={styles.methodBlock}>
              <Radio
                name="type_envoi"
                value={value}
                label={
                  <>
                    <FeaturedIcon
                      icon={Icon}
                      size="md"
                      shape="square"
                      variant={isSelected ? "solid" : "soft"}
                    />
                    <span>
                      <Text as="div" weight="bold">
                        {title}
                      </Text>
                      <Text as="div" color="muted">
                        {description}
                      </Text>
                    </span>
                    <span
                      className={classnames(
                        styles.optionCheck,
                        isSelected && styles.optionCheckSelected,
                      )}
                    >
                      <IconCheck size={16} stroke={2.5} />
                    </span>
                  </>
                }
              />
              {isSelected && (
                <>
                  {value === "lrar" && (
                    <Card>
                      <SendRecommande tenant={tenant} showTitle={false} />
                    </Card>
                  )}
                  {value === "elec" && (
                    <Card>
                      <ElectronicSigning
                        tenantProfiles={
                          caution
                            ? profiles
                            : profiles?.filter(
                                (profile) =>
                                  !profile.tmp_garantor_of &&
                                  profile.garantor_of === null &&
                                  profile.status !==
                                    ProfileStatus.Archive.status,
                              )
                        }
                        propertyId={propertyId}
                        caution={caution}
                        showTitle={false}
                      />
                    </Card>
                  )}
                  {value === "mail" && (
                    <Callout variant="info">
                      Votre document sera disponible dans l'onglet "Documents"
                      de votre locataire. Il sera également envoyé par mail à
                      votre locataire, vous serez en copie du mail.
                    </Callout>
                  )}
                  {value === "generer" && (
                    <Callout variant="info">
                      Votre document sera disponible dans l'onglet "Documents"
                      de votre locataire.
                    </Callout>
                  )}
                  {value === "numerique" && (
                    <Card>
                      <div className={styles.hints}>
                        <Title as="h3" variant="deca">
                          Signature
                        </Title>
                        <Text>
                          Faites signer chaque partie directement à l'écran,
                          avec le doigt ou la souris. Les signatures seront
                          intégrées au document, qui sera disponible dans
                          l'onglet "Documents" de votre locataire.
                        </Text>
                        <DrawnSignatures signatories={drawnSignatories ?? []} />
                        <Title as="h3" variant="deca">
                          Paraphes
                        </Title>
                        <Text>
                          Chaque partie peut également dessiner son paraphe
                          (initiales) : il sera apposé en bas de chaque page du
                          document.
                        </Text>
                        <DrawnSignatures
                          signatories={drawnSignatories ?? []}
                          name="paraphes"
                          compact
                        />
                      </div>
                    </Card>
                  )}
                </>
              )}
            </div>
          );
        })}
      </RadioGroup>
      {previewUrl && (
        <DocumentDownloadLink
          downloadUrl={previewUrl}
          mimetype="application/pdf"
          className={styles.preview}
        >
          <DocumentThumbnail
            downloadUrl={previewUrl}
            mimetype="application/pdf"
          />
        </DocumentDownloadLink>
      )}
      <Callout variant="danger">{error}</Callout>
    </div>
  );

  if (sticky) {
    return (
      <>
        <ModalDialogBody>{content}</ModalDialogBody>
        <ModalDialogFooter
          start={
            <Button
              type="button"
              variant="secondary"
              onClick={selectPreviousTab}
            >
              Précédent
            </Button>
          }
        >
          {validerButton}
        </ModalDialogFooter>
      </>
    );
  }

  return (
    <>
      <div className={styles.contentWrapper}>{content}</div>
      <div className={styles.footer}>
        <Button type="button" variant="secondary" onClick={selectPreviousTab}>
          Précédent
        </Button>
        <div className={styles.footerRight}>{validerButton}</div>
      </div>
    </>
  );
}
